import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {SameWidthButton} from '../../../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/paletteModel';
import {useToggle} from '../../../../../../srcCommon/helpers/hookHelpers';
import {isRecurringSaveTypeRequired} from '../../../../../helpers/models/recurringEventHelpers';
import {CalendarEventModel} from '../../../../../models/api/calendarEventModel';
import {
  CalendarEventVersionModel,
  CalendarEventVersionResponse,
} from '../../../../../models/api/calendarEventVersionModel';
import {ChannelModel} from '../../../../../models/api/channelModel';
import {ParticipantResponseStatusesEnum} from '../../../../../models/api/participantModel';
import {RecurringEventSaveTypesEnum} from '../../../../../store/data/calendarEventVersions/calendarEventVersionsActions';
import {RecurringResponseDialog} from '../../../../calendars/editor/recurring/recurringResponseDialog';
import {SendACommentDialog} from './sendACommentDialog';

/*
 * Props.
 */

export type CalendarEventResponseHandler = (
  newResponse: CalendarEventVersionResponse,
  recurringSaveType?: RecurringEventSaveTypesEnum,
) => void;

interface CalendarInviteViewerResponseProps {
  replyChannel: ChannelModel;
  response: CalendarEventVersionResponse;
  updateCalendarEventResponse: CalendarEventResponseHandler;
  shouldHideAddress?: boolean;
  shouldUseMultiline?: boolean;
  calendarEvent: CalendarEventModel;
  shouldShowRecurringPrompt?: boolean;
  masterCalendarEventVersion?: CalendarEventVersionModel;
}

/*
 * Style.
 */

const StyledPromptDiv = styled.div`
  line-height: 22px;
  color: ${(p) => p.theme.greys.shade70};
  ${ellipsis()};
`;

const StyledChannelSpan = styled.span`
  color: ${(p) => p.theme.greys.shade80};
  word-break: break-all;
`;

const StyledWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 2px;
  align-items: center;
`;

interface ButtonStyleProps {
  $shouldUseMultiline: boolean;
}
const StyledButtonDiv = styled.div<ButtonStyleProps>`
  justify-self: ${(p) => (p.$shouldUseMultiline ? 'start' : 'end')};
`;

/*
 * Component.
 */

export const CalendarInviteViewerResponse: FC<CalendarInviteViewerResponseProps> = (props) => {
  const {calendarEvent, replyChannel, response, shouldShowRecurringPrompt, updateCalendarEventResponse} =
    props;
  const {address, status} = response;

  // Edge case: O365 deletes calendar event when user RSVPs as not attending.
  // Therefore, if we have an O365 type reply channel and the responseStatus is not attending,
  // we should not display buttons to change response status.
  const isPermanentlyDeclined =
    !replyChannel.behaviors.canReplyToDeclinedCalendarInvite &&
    status === ParticipantResponseStatusesEnum.DECLINED;

  const prompt = buildPrompt(isPermanentlyDeclined);
  const [shouldShowSendACommentDialog, toggleSendACommentDialog] = useToggle(false);

  const [shouldShowRecurringResponseDialog, toggleRecurringResponseDialog] = useToggle(false);
  const [responseStatus, setResponseStatus] = useState<ParticipantResponseStatusesEnum>(status);
  const shouldAllowRecurringRsvp = Boolean(
    shouldShowRecurringPrompt && isRecurringSaveTypeRequired(calendarEvent),
  );

  return (
    <>
      <StyledWrapperDiv>
        <StyledPromptDiv>
          <TooltipCoordinator
            render={() => (
              <>
                {prompt}
                {` (${address})`}
              </>
            )}
          >
            <TooltipOverflowCondition>
              <StyledChannelSpan>{prompt}</StyledChannelSpan>
              {maybeRenderAddress(address, Boolean(props.shouldHideAddress))}
            </TooltipOverflowCondition>
          </TooltipCoordinator>
          {shouldShowSendACommentDialog && (
            <SendACommentDialog
              response={response}
              onSendClick={(r) => {
                toggleSendACommentDialog();
                // Reply to comment always replies to instance (if applicable)
                updateCalendarEventResponse(
                  r,
                  shouldAllowRecurringRsvp ? RecurringEventSaveTypesEnum.INSTANCE : undefined,
                );
              }}
              onCancelClick={toggleSendACommentDialog}
            />
          )}
        </StyledPromptDiv>
        {maybeRenderSendACommentButton(props, toggleSendACommentDialog)}
      </StyledWrapperDiv>
      {maybeRenderResponseButtons(
        props,
        isPermanentlyDeclined,
        shouldShowRecurringResponseDialog,
        toggleRecurringResponseDialog,
        responseStatus,
        setResponseStatus,
        shouldAllowRecurringRsvp,
      )}
    </>
  );
};

function buildPrompt(isPermanentlyDeclined: boolean) {
  if (isPermanentlyDeclined) {
    return <Trans id="invite-response-declined">You are marked as not attending</Trans>;
  }

  return <Trans id="invite-response-prompt">Are you attending?</Trans>;
}

function maybeRenderAddress(address: string, shouldHideAddress: boolean) {
  if (shouldHideAddress) {
    return null;
  }

  return ` (${address})`;
}

function maybeRenderSendACommentButton(
  props: CalendarInviteViewerResponseProps,
  toggleSendACommentDialog: Handler,
) {
  if (!props.replyChannel.behaviors.canReplyToCalendarInviteWithComment) {
    return null;
  }

  const {calendarEvent} = props;
  const {shouldIgnoreAttendeeResponse: isDisabled} = calendarEvent;

  const disabledTooltip = (
    <Trans id="invite-response-send-a-note-disabled">
      The organizer has not requested a response for this meeting
    </Trans>
  );

  return (
    <StyledButtonDiv $shouldUseMultiline={Boolean(props.shouldUseMultiline)}>
      <Button
        variant={ButtonVariantsEnum.LINK}
        size={VisualSizesEnum.MEDIUM}
        onClick={toggleSendACommentDialog}
        isDisabled={isDisabled}
        renderTooltip={isDisabled ? () => disabledTooltip : undefined}
      >
        <Trans id="invite-response-send-a-note">Send note</Trans>
      </Button>
    </StyledButtonDiv>
  );
}

function maybeRenderResponseButtons(
  props: CalendarInviteViewerResponseProps,
  isPermanentlyDeclined: boolean,
  shouldShowRecurringResponseDialog: boolean,
  toggleRecurringResponseDialog: Handler,
  responseStatus: ParticipantResponseStatusesEnum,
  setResponseStatus: HandlerOf<ParticipantResponseStatusesEnum>,
  shouldAllowRecurringRsvp: boolean,
) {
  const {response, updateCalendarEventResponse, masterCalendarEventVersion} = props;

  if (isPermanentlyDeclined) {
    return null;
  }

  const saveResponse = (
    newStatus: ParticipantResponseStatusesEnum,
    recurringSaveType?: RecurringEventSaveTypesEnum,
  ) => {
    if (!responseStatus) {
      return;
    }

    const calendarEventResponse: CalendarEventVersionResponse = {
      address: response.address,
      status: newStatus,
      comment: undefined,
    };
    updateCalendarEventResponse(calendarEventResponse, recurringSaveType);
  };

  const onResponseChange = (status: ParticipantResponseStatusesEnum) => {
    setResponseStatus(status);

    const maybeMasterResponseStatus =
      masterCalendarEventVersion &&
      masterCalendarEventVersion.response &&
      masterCalendarEventVersion.response.status;
    const newStatusIsDifferentFromMasterResponseStatus = status !== maybeMasterResponseStatus;

    // Check if we need to prompt the user for a recurringSaveType.
    // We explicitly assume that if we are updating an instance to have the same RSVP status as the
    // master, it only applies to the instance. This matches the behavior in Google Calendar.
    if (shouldAllowRecurringRsvp && newStatusIsDifferentFromMasterResponseStatus) {
      toggleRecurringResponseDialog();
      return;
    }

    saveResponse(status);
  };

  return (
    <>
      <ButtonGroup>
        {renderResponseButton(
          PaletteColorsEnum.GREEN,
          response.status === ParticipantResponseStatusesEnum.ACCEPTED,
          <Trans id="invite-response-yes">Yes</Trans>,
          () => {
            onResponseChange(ParticipantResponseStatusesEnum.ACCEPTED);
          },
        )}
        {renderResponseButton(
          PaletteColorsEnum.ORANGE,
          response.status === ParticipantResponseStatusesEnum.TENTATIVE,
          <Trans id="invite-response-maybe">Maybe</Trans>,
          () => {
            onResponseChange(ParticipantResponseStatusesEnum.TENTATIVE);
          },
        )}
        {renderResponseButton(
          PaletteColorsEnum.RED,
          response.status === ParticipantResponseStatusesEnum.DECLINED,
          <Trans id="invite-response-no">No</Trans>,
          () => {
            onResponseChange(ParticipantResponseStatusesEnum.DECLINED);
          },
        )}
      </ButtonGroup>
      {maybeRenderRecurringResponsePrompt(
        shouldShowRecurringResponseDialog,
        toggleRecurringResponseDialog,
        responseStatus,
        saveResponse,
      )}
    </>
  );
}

function renderResponseButton(
  color: PaletteColorsEnum,
  isSelected: boolean,
  content: ReactNode,
  onClick: Handler,
) {
  const variant = isSelected ? ButtonVariantsEnum.CUSTOM : ButtonVariantsEnum.SECONDARY;
  return (
    <SameWidthButton color={color} variant={variant} onClick={onClick} isDisabled={isSelected}>
      {content}
    </SameWidthButton>
  );
}

function maybeRenderRecurringResponsePrompt(
  shouldShowRecurringResponseDialog: boolean,
  toggleRecurringResponseDialog: Handler,
  responseStatus: ParticipantResponseStatusesEnum,
  saveResponse: (
    responseStatus: ParticipantResponseStatusesEnum,
    recurringSaveType?: RecurringEventSaveTypesEnum,
  ) => void,
) {
  if (!shouldShowRecurringResponseDialog) {
    return null;
  }

  return (
    <RecurringResponseDialog
      onConfirmClick={(recurringSaveType) => {
        toggleRecurringResponseDialog();
        saveResponse(responseStatus, recurringSaveType);
      }}
      onCancelClick={() => toggleRecurringResponseDialog()}
    />
  );
}
